import type { ParametersRecord } from './parameter-value'
import type { NodeFace, SummaryContext } from './universal-node-schema'

/**
 * The words nodes use for themselves and their wires: the builders behind the
 * `shortName`, `name` and `summary` a node's face declares (schemas/node-face.ts),
 * read through components/nodes/node-utils.ts. One place per node, so a pill,
 * a form operand and an exposed-setting alias always say the same thing.
 */

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

/** "Close-5m": the field the receiving input picked, on this feed's timeframe. */
export const ohlcvSummary = ({ params, consumerField }: SummaryContext) =>
  `${capitalize(consumerField)}-${(params['timeframe'] as string | undefined) || '1m'}`

/** "SMA(20)": a moving average with its period. */
export const movingAverageName = (short: string) => (params: ParametersRecord) =>
  `${short}(${params['period'] ?? params['periods'] ?? 20})`

/** "RSI(14)": an oscillator with its period. */
export const periodName = (short: string, fallback: number) => (params: ParametersRecord) =>
  `${short}(${params['period'] ?? fallback})`

/** "BB(20,2)": period and band width. */
export const bollingerName = (params: ParametersRecord) =>
  `BB(${params['period'] ?? 20},${params['stdDev'] ?? 2})`

/** "MACD(12,26)": fast and slow periods. */
export const macdName = (params: ParametersRecord) =>
  `MACD(${params['period_me1'] ?? 12},${params['period_me2'] ?? 26})`

/** Input B as typed (a static value) or as wired. */
const operandB = ({ params, input, staticValue }: SummaryContext) => {
  if (((params['inputBType'] as string | undefined) || 'node') !== 'static') return input('input_2')
  const value = staticValue('input_2')
  return value !== undefined ? String(value) : '0'
}

/** "Close-5m > SMA(9)". */
export const conditionSummary = (context: SummaryContext) =>
  `${context.input('input_1')} ${(context.params['operator'] as string | undefined) || '>'} ${operandB(context)}`

/** "Close-5m ↗ SMA(9)": × either way, ↗ above, ↘ below. */
export const crossoverSummary = ({ params, input }: SummaryContext) => {
  const crossType = (params['crossType'] as string | undefined) || 'crosses'
  const symbol = crossType === 'crosses-above' ? '↗' : crossType === 'crosses-below' ? '↘' : '×'
  return `${input('input_1')} ${symbol} ${input('input_2')}`
}

/** "{Close-5m > SMA(9) ≥ 3} Count". */
export const conditionCounterSummary = (context: SummaryContext) => {
  const operator = (context.params['operator'] as string | undefined) || '>='
  const shown =
    operator === '>=' ? '≥' : operator === '<=' ? '≤' : operator === '!=' ? '≠' : operator
  return `{${context.input('input_1')} ${shown} ${operandB(context)}} Count`
}

/** "Close-5m[-2]": the input, so many bars back. */
export const barOffsetSummary = ({ params, input }: SummaryContext) =>
  `${input('input_1')}[${-((params['offset'] as number | undefined) || 1)}]`

/** Whatever the active input carries; it follows a flip of `activeInput`. */
export const switcherSummary = ({ params, input }: SummaryContext) =>
  input(`input_${Number((params['activeInput'] as number | string | undefined) ?? 0) + 1}`)

/** "AND (2)": the operator and how many wires it combines. */
export const mergeSummary = ({ params, wiredInputs }: SummaryContext) =>
  `${(params['operator'] as string | undefined) || 'AND'} (${wiredInputs})`

/** "09:30 - 16:00". */
export const timeSignalSummary = ({ params }: SummaryContext) =>
  `${(params['startTime'] as string | undefined) || '09:30'} - ${(params['endTime'] as string | undefined) || '16:00'}`

const TIME_SLICE_MODES: Record<string, string> = {
  first: 'First',
  last: 'Last',
  max: 'Max',
  min: 'Min',
}

/** "High-5m - Max": what is sliced and how it is reduced. */
export const timeSliceSummary = ({ params, input }: SummaryContext) => {
  const upstream = input('input_1')
  const mode = (params['mode'] as string | undefined) || 'last'
  const modeText = TIME_SLICE_MODES[mode] ?? capitalize(mode)
  const hasUpstream = upstream && upstream !== 'No Input' && upstream !== 'Connected'
  return hasUpstream ? `${upstream} - ${modeText}` : `Time Slice - ${modeText}`
}

/**
 * A formula over the lettered inputs ("A + B"), each letter naming what is
 * wired into that input (input_1 is A) or the number typed into it, as the
 * Condition's check does (Chris, 2026-10-06: "SMA(20) − Close-5m"). A letter
 * stays where nothing is wired, and every letter stays without a node to read.
 */
export const formula =
  (text: string) =>
  (context?: Pick<SummaryContext, 'input' | 'staticValue'>): string => {
    if (!context) return text
    return text.replace(/(?<![A-Za-z])([A-H])(?![A-Za-z])/g, (letter) => {
      const handle = `input_${letter.charCodeAt(0) - 64}`
      const typed = context.staticValue(handle)
      if (typed !== undefined && typed !== null && typed !== '') return String(typed)
      const name = context.input(handle)
      return !name || name === handle || name === 'No Input' ? letter : name
    })
  }

/** "A + B + C": the lettered inputs, the wired ones but never fewer than two. */
const lettered = (wiredInputs: number, separator: string) =>
  Array.from({ length: Math.max(2, wiredInputs) }, (_, i) => String.fromCharCode(65 + i)).join(
    separator,
  )

export const sumEquation = ({ wiredInputs }: SummaryContext) => lettered(wiredInputs, ' + ')

export const averageEquation = ({ wiredInputs }: SummaryContext) =>
  `(${lettered(wiredInputs, ' + ')}) ÷ ${Math.max(2, wiredInputs)}`

export const maxEquation = ({ wiredInputs }: SummaryContext) =>
  `MAX(${lettered(wiredInputs, ', ')})`

export const minEquation = ({ wiredInputs }: SummaryContext) =>
  `MIN(${lettered(wiredInputs, ', ')})`

/** "Output = A": the input the switch passes on. */
export const switcherEquation = ({ params }: SummaryContext) =>
  `Output = ${String.fromCharCode(65 + Number(params['activeInput'] ?? 0))}`

/**
 * Node types with no schema that still turn up in old saved strategies
 * (kebab-case and short spellings, retired filters). Same builders, so they
 * read as they always have.
 */
export const LEGACY_FACES: Record<string, Partial<NodeFace>> = {
  'price-data': { shortName: 'OHLCV-1m', summary: ohlcvSummary },
  'market-data': { shortName: 'Market Data', summary: ohlcvSummary },
  volumeData: { shortName: 'Volume Data', summary: ohlcvSummary },
  'volume-data': { shortName: 'Volume Data', summary: ohlcvSummary },
  sma: { shortName: 'SMA', name: movingAverageName('SMA') },
  ema: { shortName: 'EMA', name: movingAverageName('EMA') },
  wma: { name: movingAverageName('WMA') },
  rsi: { shortName: 'RSI', name: periodName('RSI', 14) },
  'bollinger-bands': { shortName: 'BB', name: bollingerName },
  'bar-offset': { shortName: 'OFFSET', summary: barOffsetSummary },
  'bars-condition-counter': { shortName: 'COUNTER', summary: conditionCounterSummary },
  rollingaverage: { shortName: 'ROLLING-AVG' },
  rollingmaximum: { shortName: 'ROLLING-MAX' },
  rollingmidpoint: { shortName: 'ROLLING-MID' },
  rollingminimum: { shortName: 'ROLLING-MIN' },
  rollingsum: { shortName: 'ROLLING-SUM' },
  rollingrange: { shortName: 'ROLLING-RNG' },
  timeslice: { summary: timeSliceSummary },
  'time-signal': { shortName: 'Time Signal', summary: timeSignalSummary },
  'price-change': { shortName: 'Price Change' },
  'position-in-range': { shortName: 'Position In Range' },
  'volume-filter': { shortName: 'Volume Filter' },
  dailyPercentChange: { shortName: 'Daily %' },
  sessionPercentChange: { shortName: 'Session %' },
  weeklyPercentChange: { shortName: 'Weekly %' },
  monthlyPercentChange: { shortName: 'Monthly %' },
  yearlyPercentChange: { shortName: 'Yearly %' },
  customPeriodPercentChange: { shortName: 'Custom %' },
  totalVolume: { shortName: 'Total Volume' },
  sessionVolume: { shortName: 'Session Volume' },
  customPeriodVolume: { shortName: 'Custom Volume' },
  percentChangeFromOpen: { shortName: '% From Open' },
  percentChangeFromClose: { shortName: '% From Close' },
  percentChangeFromHigh: { shortName: '% From High' },
  percentChangeFromLow: { shortName: '% From Low' },
}
